import { ReactNode } from 'react';
import { StatsCardConfig, getStatsCardClasses } from '../config/statusConfig';

interface StatsCardProps {
  value: number | string;
  label: string;
  config: StatsCardConfig;
  isActive?: boolean;
  onClick?: () => void;
  icon?: ReactNode;
}

/**
 * Einheitliche Stats-Card Komponente
 *
 * Design-System:
 * - Kompakte Höhe (p-3)
 * - 4px linker Akzent-Border in Status-Farbe
 * - Zahl oben (text-2xl font-bold, linksbündig)
 * - Label unten (text-xs, linksbündig)
 * - Optional: Icon links neben der Zahl
 */
export function StatsCard({
  value,
  label,
  config,
  isActive = false,
  onClick,
  icon,
}: StatsCardProps) {
  const classes = getStatsCardClasses(config, isActive);
  const isClickable = !!onClick;

  const content = (
    <>
      <div className="flex items-center gap-2">
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span className={`text-2xl font-bold ${config.text}`}>{value}</span>
      </div>
      <div className={`text-xs ${config.text} opacity-75 mt-0.5`}>{label}</div>
    </>
  );

  if (isClickable) {
    return (
      <button
        onClick={onClick}
        className={`${classes} text-left w-full cursor-pointer`}
      >
        {content}
      </button>
    );
  }

  return <div className={classes}>{content}</div>;
}

/**
 * Stats-Card Grid Container
 * Responsive: 2 Spalten auf Mobile, bis zu 6 auf Desktop
 */
export function StatsCardGrid({
  children,
  columns = 'auto',
}: {
  children: ReactNode;
  columns?: 'auto' | 2 | 3 | 4 | 5 | 6;
}) {
  const colClasses = {
    auto: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
    2: 'grid-cols-2',
    3: 'grid-cols-2 sm:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-4',
    5: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
    6: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6',
  };

  return (
    <div className={`grid ${colClasses[columns]} gap-3`}>
      {children}
    </div>
  );
}

export default StatsCard;
